import { localized, msg, str } from "@lit/localize";
import { html, LitElement, nothing } from "lit";
import { customElement, property, query, queryAssignedNodes, state } from 'lit/decorators.js';
import { styleMap } from "lit/directives/style-map.js";
import { when } from "lit/directives/when.js";
import style from './usos-tooltip.scss';
import "../icon";
import { supportsPopover } from "../utils/support";

/**
 * Simple tooltip as (i) icon with popup text on hover/focus and persisting on click.
 *
 * @slot - tooltip contents
 */
@customElement('usos-tooltip')
@localized()
export class UsosTooltip extends LitElement {
    static override styles = style;

    static allElements: UsosTooltip[] = [];

    static hideAll(except: UsosTooltip | null = null, hidePersistent = false) {
        this.allElements.forEach(el => {
            if (el !== except) {
                if (hidePersistent)
                    el._persistent = false;
                el.hide();
            }
        })
    }

    private static usePopover = supportsPopover();

    override connectedCallback(): void {
        super.connectedCallback();
        UsosTooltip.allElements.push(this);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        const thisIndex = UsosTooltip.allElements.indexOf(this);
        if (thisIndex != -1) UsosTooltip.allElements.splice(thisIndex, 1);
    }

    /** Tooltip position. 'auto' means "top if possible, bottom otherwise" */
    @property({reflect: true}) position: 'top' | 'bottom' | 'auto' = 'auto';

    /**
     * If set to `true`, the tooltip will not calculate its width.
     * User must then style the component with proper fixed width.
     */
    @property({attribute: 'const-size', type: Boolean, reflect: true}) constSize = false;

    @state() private _visible = false;
    @state() private _persistent = false;
    @state() private _targetWidth = this._simulateMaxContentWidth('');

    @query('#tooltip-icon') private _tooltipIcon?: HTMLElement;

    @query('#tooltip')
    private _tooltip?: HTMLElement;

    @queryAssignedNodes() private _slotContent?: Node[];

    show(e: Event) {
        e.stopPropagation();
        if (e.type == 'click') {
            this._persistent = true;
            UsosTooltip.hideAll(this, true);
        }
        if (UsosTooltip.usePopover) {
            this._tooltip?.showPopover();
        }
        this._visible = true;
    }

    hide(e?: Event) {
        e?.stopPropagation();
        if (!this._persistent) {
            this._visible = false;
            if (UsosTooltip.usePopover) {
                this._tooltip?.hidePopover();
            }
        }
    }

    override render() {
        const position = this._calculateTooltipPosition(this._targetWidth);
        const arrowPosition = -position;
        const layoutPosition = (this.position == 'auto') ? this._calculateLayoutPosition() : this.position;
        const arrowStyle = {
            marginLeft: `${arrowPosition}px`,
        };
        let tooltipStyle: Record<string, string | null> = {};
        if (UsosTooltip.usePopover) {
            tooltipStyle = this._visible ? this._calculatePopoverPosition(this._targetWidth, layoutPosition) : {};
            tooltipStyle.width = `${this._targetWidth}px`;
        } else {
            tooltipStyle = {
                width: (this._visible) ? `${this._targetWidth}px` : null,
                left: `${position}px`,
            };
        }
        const tooltipArrowTemplate = () => html`<div id="tooltip-arrow" style=${styleMap(arrowStyle)} data-position=${layoutPosition}></div>`;
        /* eslint-disable lit-a11y/click-events-have-key-events */
        return html`
            <div @mouseover=${this.show} @focus=${this.show} @blur=${this.hide} @click=${this.show} @mouseout=${this.hide} tabindex="0">
                <usos-icon id="tooltip-icon" icon-name="info" icon-type="outlined" .label=${msg(str`Podpowiedź`)} aria-describedby="tooltip-content"></usos-icon>
                <div id="tooltip" style=${styleMap(tooltipStyle)} data-visible=${this._visible} data-position=${layoutPosition} popover=${UsosTooltip.usePopover ? 'manual' : nothing}>
                    ${when(layoutPosition == 'bottom', tooltipArrowTemplate)}
                    <div id="tooltip-content" role="tooltip" aria-label=${msg(str`Podpowiedź`)}>
                        <slot @slotchange=${this._onSlotChange}></slot>
                    </div>
                    ${when(layoutPosition == 'top', tooltipArrowTemplate)}
                </div>
            </div>
        `;
        /* eslint-enable lit-a11y/click-events-have-key-events */
    }

    /**
     * Notify the tooltip that its content has changed and it should recalculate its width.
     */
    notifyContentChanged() {
        this._targetWidth = this._calculateWidth() ?? this._targetWidth;
    }

    private _onSlotChange() {
        this.notifyContentChanged();
    }

    /**
     * Simulate tooltip width if content is displayed in one line
     */
    private _simulateMaxContentWidth(textContent: string) {
        const div = document.createElement('div');
        div.textContent = textContent;
        document.body.appendChild(div);

        // styling similar to screen-reader-only...
        div.style.opacity = '0';
        div.style.position = 'absolute';
        div.style.top = '-1000rem';

        // ... except we want to get width for one-liner
        div.style.width = 'max-content';
        div.style.whiteSpace = 'nowrap';

        // and styled as similar to #tooltip-content as possible
        div.style.fontSize = '0.8125em';
        div.style.padding = '6px 14px';
        div.style.border = '1px solid';

        const width = div.getBoundingClientRect().width;
        document.body.removeChild(div);

        return width;
    }

    /**
     * Calculate target width.
     *
     * @returns Target width in pixels or null if const sizing is set
     */
    private _calculateWidth(): number | null {
        if (this.constSize) return null;

        const maxWidth = document.body.clientWidth;
        const slotContent = this._slotContent?.flatMap(
            el => el instanceof HTMLSlotElement
                ? el.assignedNodes()
                : el
        )
        const textContent = slotContent?.map(el => el.textContent?.trim() ?? '').filter(tC => tC != '').join(' ') ?? '';
        const contentLength = textContent.length;

        // copied from jquery-usos
        let contentWidth: number;
        if (contentLength < 300) {
            // for really short tooltip content 300px is too much
            const maxWidthContent = this._simulateMaxContentWidth(textContent);
            contentWidth = Math.min(300, maxWidthContent);
        } else if (contentLength < 1200) {
            /* We don't want it to be too high, so it is better to make it wider. */
            const scale = 1.0 - ((1200 - contentLength) / 900.0);
            contentWidth = 300 + 300 * scale;
        } else {
            contentWidth = 600;
        }

        return Math.min(maxWidth, contentWidth);
    }

    private _calculateTooltipPosition(width: number): number {
        const iconBounds = this._tooltipIcon?.getBoundingClientRect();
        const iconMidpoint = iconBounds ? (iconBounds.right + iconBounds.left) / 2 : 0;
        if (width / 2 >= iconMidpoint)
            return -(iconBounds?.left ?? 0);
        else if (document.body.clientWidth < iconMidpoint + width / 2)
            return -width + (document.body.clientWidth - iconMidpoint);
        else
            return -width / 2;
    }

    private _calculatePopoverPosition(width: number, position: LayoutParams.VerticalPosition): {top?: string, left?: string} {
        const spacingPx = 4;
        const iconBounds = this._tooltipIcon?.getBoundingClientRect();
        if (iconBounds == null) {
            return {};
        }
        let top = iconBounds.top + window.scrollY;
        if (position == 'top')
            top -= this._tooltip!.clientHeight + spacingPx;
        if (position == 'bottom')
            top += iconBounds.height + spacingPx;
        const left = iconBounds.left + window.scrollX + this._calculateTooltipPosition(width);
        return {top: `${top}px`, left: `${left}px`};
    }

    private _calculateLayoutPosition(): LayoutParams.VerticalPosition {
        return this.getBoundingClientRect().top >= (this._tooltip?.clientHeight ?? 0) ? 'top' : 'bottom';
    }
}

document.addEventListener('click', () => {
    UsosTooltip.hideAll(null, true);
});

declare global {
    interface HTMLElementTagNameMap {
        "usos-tooltip": UsosTooltip,
    }
}
